import React, { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useOptionalNotifications } from '../../context/NotificationContext';
import type { Notification } from '../../lib/notificationApi';

/**
 * What a Confirm & sync did to the Master Catalogue, at the top of the page
 * (QA USC-BUG-0061). The catalogue sync runs after the confirm returns, so its
 * result arrives as a Notification (`metadata.kind`
 * 'vendor_invoice_catalogue_sync'); before this it reached only the bell, and a
 * reviewer never learned that a line was skipped or a packing alert raised.
 *
 * `packingAlerts` is what the confirm itself returned. It is always shown once
 * given - an empty list reads "no packing alerts", which is an answer too.
 */

export type ConfirmPackingAlert = {
  index: number;
  lineNo: number;
  description: string;
  kind: string;
  message: string;
};

export const CATALOGUE_SYNC_KIND = 'vendor_invoice_catalogue_sync';

type SyncMeta = {
  kind?: string;
  invoiceId?: string | null;
  upserted?: number;
  skipped?: number;
  failed?: boolean;
  packingAlerts?: ConfirmPackingAlert[];
};

const meta = (n: Notification) => (n.metadata || {}) as SyncMeta;

/**
 * The newest UNREAD catalogue-sync notification, for one invoice or (no id) any.
 * Unread only: Dismiss marks it read, which clears it here and in the bell.
 */
export function latestCatalogueSync(
  notifications: Notification[],
  invoiceId: string | null
): Notification | null {
  const found = notifications
    .filter((n) => meta(n).kind === CATALOGUE_SYNC_KIND && !n.isRead)
    .filter((n) => !invoiceId || String(meta(n).invoiceId) === invoiceId)
    .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
  return found[0] || null;
}

export default function CatalogueSyncBanner(props: {
  /** The invoice to report on; null = the newest unread sync (Master Catalogue page). */
  invoiceId: string | null;
  /** From the confirm result; undefined when this screen did not run the confirm. */
  packingAlerts?: ConfirmPackingAlert[];
  /** The confirm just ran here, so a missing notification means "still running". */
  awaitingSync?: boolean;
  onDismiss?: () => void;
  /** Which invoice this is about, when shown outside that invoice (the list page). */
  label?: string;
}) {
  const { invoiceId, packingAlerts, awaitingSync = false, onDismiss, label } = props;
  const ctx = useOptionalNotifications();
  const note = ctx ? latestCatalogueSync(ctx.notifications, invoiceId) : null;
  const pending = awaitingSync && !note;
  const refresh = ctx?.refresh;

  // The provider polls every 30 s; while the sync is known to be running, ask
  // sooner, for a minute at most.
  useEffect(() => {
    if (!pending || !refresh) return;
    const tick = setInterval(() => void refresh(), 5000);
    const stop = setTimeout(() => clearInterval(tick), 60000);
    return () => {
      clearInterval(tick);
      clearTimeout(stop);
    };
  }, [pending, refresh]);

  if (!note && !pending && packingAlerts === undefined) return null;

  const m = note ? meta(note) : null;
  const alerts = packingAlerts ?? m?.packingAlerts ?? [];
  const failed = !!m?.failed;
  const upserted = Number(m?.upserted || 0);
  const skipped = Number(m?.skipped || 0);
  const warn = failed || skipped > 0 || alerts.length > 0;

  const dismiss = () => {
    if (note && !note.isRead && ctx) void ctx.markAsRead(note._id);
    onDismiss?.();
  };

  // One compact line (2026-09-25): the two-row box sat inside the invoice's
  // action bar and pushed every button onto a second row. The alert list still
  // follows underneath when there is one.
  return (
    <div
      role="status"
      aria-live="polite"
      className={`w-full rounded-md border px-3 py-1.5 text-xs ${
        warn ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-emerald-300 bg-emerald-50 text-emerald-900'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
          {label ? <span className="font-semibold">{label}</span> : null}
          <span className="inline-flex items-center gap-1.5">
            {pending ? <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" /> : null}
            <span className="font-semibold">Master Catalogue: </span>
            {pending
              ? 'sync is running. The result appears here in a moment.'
              : failed
                ? 'the sync failed, so nothing was added. The stock-in itself went through.'
                : m
                  ? `${upserted} entr${upserted === 1 ? 'y' : 'ies'} added or updated` +
                    (skipped ? `, ${skipped} line${skipped === 1 ? '' : 's'} skipped` : '') +
                    '.'
                  : 'result not reported yet.'}
          </span>
          <span>
            <span className="font-semibold">Packing: </span>
            {alerts.length ? `${alerts.length} alert${alerts.length === 1 ? '' : 's'} to check.` : 'no packing alerts.'}
          </span>
        </div>
        <button type="button" onClick={dismiss} className="underline underline-offset-2 hover:no-underline">
          Dismiss
        </button>
      </div>
      {alerts.length > 0 && (
        <ul className="mt-1 list-disc pl-5">
          {alerts.map((a, i) => (
            <li key={`${a.index}-${a.kind}-${i}`}>
              Line {a.lineNo}
              {a.description ? ` (${a.description})` : ''}: {a.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
